import { ActionSheetButton, IonActionSheet, IonButton, IonButtons, IonIcon, IonItem } from '@ionic/react';
import { close, menu, pencil, trash, sync } from 'ionicons/icons';
import React, { FC, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { GenericMessages } from 'src/intl/GenericMessages';
import { JobMessages } from 'src/intl/JobMessages';
import { useBreakpoint } from 'src/utils/helpers/platformHelpers';
import { useSocketGet } from 'src/utils/socket';
import { SocketContextPortal } from 'src/utils/socket/context/portalSocketContext';
import { DqText } from '../dq-text';
import './AnnotationRow.scss';
import { AnnotationViewModel } from './AnnotationsContext';
import { User } from '../../models/user';
import { canDeleteAnnotation, canEditAnnotation, usePolicy } from '../../models/policies';

export interface AnnotationRowProps {
  /** The annotation record to display. */
  annotation: AnnotationViewModel;
  /** The function to execute when a user updates the annotation. */
  onUpdate: (annotation: AnnotationViewModel) => void;
  /** The function to execute when a user deletes the annotation. */
  onDelete: (annotation: AnnotationViewModel) => void;
  /** The function to execute when a user restores a recently deleted message. */
  onRestore: (annotation: AnnotationViewModel) => void;
  /** Whether the message has been deleted after the most recent page refresh. */
  deletedAfterLastPageRefresh: boolean;
  /** Indicates if timestamps should be displayed. */
  showTimestamps: boolean;
  /** Indicates if action buttons should be displayed. */
  showAnnotationActions: boolean;
  /** Indicates if the annotation author should be displayed, or the organization they belong to if false. */
  showAnnotationUser: boolean;
  /** User name to show when not permitted to display real users */
  fallbackUser: string;
  /** The timezone associated with the job log. */
  timezone: string;
}

function computeUpdatedBy(
  annotation: AnnotationViewModel,
  showAnnotationUser: boolean,
  updatedByUser: User | undefined,
  formatMessage: IntlShape['formatMessage'],
  fallbackUser: string,
) {
  const isUpdated = annotation.updatedAt && annotation.updatedAt !== annotation.createdAt;

  if (!isUpdated) {
    return undefined;
  }

  if (!showAnnotationUser) return fallbackUser;
  if (updatedByUser) return `${updatedByUser.given_name} ${updatedByUser.family_name}`;
  return formatMessage(GenericMessages.unknownPlaceholder);
}

function computeCreatedBy(
  annotation: AnnotationViewModel,
  showAnnotationUser: boolean,
  createdByUser: User | undefined,
  formatMessage: IntlShape['formatMessage'],
  fallbackUser: string,
) {
  if (!showAnnotationUser) return fallbackUser;
  if (createdByUser) return `${createdByUser.given_name} ${createdByUser.family_name}`;
  return formatMessage(GenericMessages.unknownPlaceholder);
}

export const AnnotationRow: FC<AnnotationRowProps> = ({
  annotation,
  onUpdate,
  onDelete,
  onRestore,
  deletedAfterLastPageRefresh,
  showTimestamps,
  showAnnotationActions,
  showAnnotationUser,
  fallbackUser,
  timezone,
}) => {
  const { formatMessage, locale } = useIntl();
  const [showActionSheet, setShowActionSheet] = useState(false);
  const { item: createdByUser } = useSocketGet(SocketContextPortal, 'users', annotation.createdBy);
  const { item: updatedByUser } = useSocketGet(SocketContextPortal, 'users', annotation.updatedBy);
  const isMd = useBreakpoint('md');
  const isEditable = usePolicy(canEditAnnotation, annotation);
  const isDeletable = usePolicy(canDeleteAnnotation, annotation);

  const createdBy = computeCreatedBy(annotation, showAnnotationUser, createdByUser, formatMessage, fallbackUser);
  const updatedBy = computeUpdatedBy(annotation, showAnnotationUser, updatedByUser, formatMessage, fallbackUser);

  const actions = React.useMemo(() => {
    const result: ActionSheetButton[] = [];
    if (!showAnnotationActions) return result;

    if (isEditable) {
      result.push({
        id: 'edit',
        text: formatMessage(GenericMessages.edit),
        icon: pencil,
        handler: () => {
          onUpdate(annotation);
        },
      });
    }
    if (isDeletable) {
      result.push({
        id: 'delete',
        text: formatMessage(GenericMessages.delete),
        icon: trash,
        data: {
          type: 'delete',
        },
        handler: () => {
          onDelete(annotation);
        },
      });
    }
    if (result.length > 0) {
      result.push({
        id: 'cancel',
        text: formatMessage(GenericMessages.cancelButtonLabel),
        icon: close,
        role: 'cancel',
        handler: () => {
          setShowActionSheet(false);
        },
      });
    }
    return result;
  }, [annotation, formatMessage, isDeletable, isEditable, showAnnotationActions, onDelete, onUpdate]);

  if (annotation.deletedOn) {
    return (
      <IonItem lines="none" className="annotation-row">
        <DqText style={{ whiteSpace: 'pre-line' }}>{formatMessage(JobMessages.commentHasBeenDeleted)}</DqText>
        {deletedAfterLastPageRefresh && (
          <IonButton color="secondary" fill="clear" onClick={() => onRestore(annotation)}>
            {formatMessage(JobMessages.undo)}
          </IonButton>
        )}
      </IonItem>
    );
  }

  return (
    <IonItem lines="none" className="annotation-row">
      <div>
        <DqText className="annotation-row-header" variant="body-small">
          {annotation.isPendingSync && (
            <IonIcon className="annotation-row-header-sync" size="small" icon={sync} color="warning" />
          )}
          <DqText variant="body-strong">{createdBy}&nbsp;&nbsp;&nbsp;</DqText>
          {showTimestamps && (
            <DqText variant="body-small">
              <span>
                {annotation.timestamp && '◉ '}
                {new Date(annotation.timestamp ?? annotation.createdAt).toLocaleString(locale, {
                  timeZone: timezone,
                  timeZoneName: 'short',
                })}
              </span>
              {!!updatedBy && (
                <span className="annotation-row-edited-by">
                  &nbsp;({formatMessage(GenericMessages.editedBy, { userName: updatedBy })})
                </span>
              )}
            </DqText>
          )}
        </DqText>
        <div>
          <DqText>{annotation.comment}</DqText>
        </div>
      </div>
      {actions.length ? (
        <IonButtons slot="end">
          {isMd ? (
            actions.map(i => {
              if (i.role === 'cancel') return null;
              return (
                <IonButton key={i.id} fill="clear" onClick={i.handler} color="primary">
                  <IonIcon slot="icon-only" icon={i.icon} />
                </IonButton>
              );
            })
          ) : (
            <IonButton onClick={() => setShowActionSheet(true)} fill="clear" color="primary">
              <IonIcon slot="icon-only" icon={menu} />
            </IonButton>
          )}
        </IonButtons>
      ) : null}

      <IonActionSheet isOpen={showActionSheet} onDidDismiss={() => setShowActionSheet(false)} buttons={actions} />
    </IonItem>
  );
};
